"use client";

import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Default icon registry used across the app
// Extend this map as needed
export const DEFAULT_ICONS = {
  arrowRight: byPrefixAndName.far["arrow-right"],
  arrowLeft: byPrefixAndName.far["arrow-left"],
  chevronDown: byPrefixAndName.far["chevron-down"],
  chevronUp: byPrefixAndName.far["chevron-up"],
  info: byPrefixAndName.far["circle-info"],
  check: byPrefixAndName.far["check"],
  close: byPrefixAndName.far["xmark"],
};

/**
 * IconWrapper
 * A flexible icon wrapper component around FontAwesomeIcon that:
 * - Lets you use a default icon by `name` from DEFAULT_ICONS
 * - Or pass a custom `icon` definition directly (overrides name)
 * - Keeps a consistent API and accessibility defaults
 *
 * Usage examples:
 *
 * 1) Use a default icon by name
 *    <IconWrapper name="arrowRight" className="w-5 h-5 text-primary" />
 *
 * 2) Pass a custom icon definition
 *    <IconWrapper icon={byPrefixAndName.far["arrow-right"]} className="w-5 h-5" />
 *
 * 3) Accessibility
 *    <IconWrapper name="info" ariaLabel="Info" />
 *    // If ariaLabel is not provided, icon is hidden from screen readers
 */
export default function IconWrapper({
  name,
  icon,
  className = "",
  size,
  color,
  title,
  ariaLabel,
  role = "img",
  sizePx,
  widthPx,
  heightPx,
  style,
  ...rest
}) {
  const resolvedIcon = icon || (name ? DEFAULT_ICONS[name] : undefined);

  if (!resolvedIcon) {
    // Graceful no-op render to avoid crashes if icon not found
    return null;
  }

  const accessibilityProps = ariaLabel
    ? { "aria-label": ariaLabel }
    : { "aria-hidden": true };

  const mergedStyle = {
    ...(style || {}),
    ...(typeof sizePx === "number" ? { fontSize: `${sizePx}px` } : {}),
    ...(typeof widthPx === "number" ? { width: `${widthPx}px` } : {}),
    ...(typeof heightPx === "number" ? { height: `${heightPx}px` } : {}),
  };

  return (
    <FontAwesomeIcon
      icon={resolvedIcon}
      className={className}
      size={size}
      color={color}
      title={title}
      role={role}
      style={mergedStyle}
      {...accessibilityProps}
      {...rest}
    />
  );
}


